import { Autobind, Define, Light, SetAttrs } from '../../../core/decorators';
import Log from '../../../core/utils/Log';
import BnumElementInternal from '../../bnum-element-states';
import { HTMLBnumFolder } from '../../molecules/folder/bnum-folder';
import { TAG_TREE } from 'core/utils/constants/tags';
import style from './bnum-tree.less';
import { Result, Risky } from '@rotomeca/rop';

//#region Global Constants
const ATTR_SELECTED = 'is-selected';
const ATTR_COLLAPSED = 'is-collapsed';
const ROLE_ITEM = '[role="treeitem"]';
//#endregion Global Constants

/**
 * Webcomposant représentant un arbre.
 *
 * @category Group
 */
@Define({ tag: TAG_TREE })
@Light()
export class HTMLBnumTree extends BnumElementInternal {
	//#region Private Fields
	#_selectedItem: HTMLElement | null = null;
	#_focusedItem: HTMLElement | null = null;
	//#endregion Private Fields

	//#region Lifecycle
	constructor() {
		super();
	}

	@SetAttrs({
		role: 'tree',
		tabindex: '0',
	})
	protected _p_attach(): void {
		super._p_attach();

		if (!this.attr('aria-label') && !this.attr('aria-labellerby')) {
			Log.warn(
				'HTMLBnumTree',
				"Un arbre doit avoir un attribut aria-label ou aria-labelledby pour des raisons d'accessibilité.",
				'Un texte par défaut a été ajouté.',
			);

			this.attr('aria-label', 'Arbre perdu dans la forêt');
		}
	}

	protected _p_DOM(): void {
		super._p_DOM();
		this.#_initListeners().#_initializeRovingTabindex();
	}

	protected _p_detach(): void {
		super._p_detach();
		this.#_clearListeners();

		this.#_selectedItem = null;
		this.#_focusedItem = null;
	}
	//#endregion Lifecycle

	//#region Public Methods
	/**
	 * Méthode publique pour sélectionner un item programmatiquement
	 * @param item L'élément à sélectionner
	 */
	public SelectItem(item: HTMLElement): void {
		if (!item || !item.isConnected) return;

		// 1. Désélection de l'ancien (O(1))
		if (this.#_selectedItem && this.#_selectedItem !== item) {
			this.#_selectedItem.setAttribute(ATTR_SELECTED, 'false');
		} else if (!this.#_selectedItem) {
			// Si aucun élément n'était sélectionné auparavant
			this.querySelectorAll(`[${ATTR_SELECTED}="true"]`).forEach(el => {
				el.setAttribute(ATTR_SELECTED, 'false');
			});
		}
		// 2. Sélection du nouveau
		item.setAttribute(ATTR_SELECTED, 'true');
		this.#_selectedItem = item;

		// 3. Mise à jour du focus clavier (Roving Tabindex)
		this.#_updateFocus(item);

		// 4. Notification pour le reste de l'application
		this.trigger('bnum-tree:change', { item });
	}

	/**
	 * Ajoute des nodes à l'arbre.
	 *
	 * Les nodes de type texte sont enveloppés dans un span avec le rôle treeitem.
	 *
	 * Les éléments HTML qui n'ont pas le rôle treeitem se voient attribuer ce rôle.
	 * @param nodes Nodes à ajouter.
	 * @returns L'instance courante.
	 */
	append(...nodes: (Node | string)[]): this {
		const arrayOfNodes = [];
		for (const node of nodes) {
			if (typeof node === 'string') {
				Log.warn(
					'HTMLBnumTree',
					"L'ajout direct de texte dans un arbre n'est pas autorisé. L'élément est envellopper dans un span !.",
				);
				arrayOfNodes.push(
					this._p_createSpan({ child: node, attributes: { role: 'treeitem' } }),
				);
			} else if (
				node instanceof HTMLElement &&
				node.getAttribute('role') === 'group'
			) {
				arrayOfNodes.push(node);
			} else if (
				node instanceof HTMLElement &&
				node.getAttribute('role') !== 'treeitem'
			) {
				node.setAttribute('role', 'treeitem');
				arrayOfNodes.push(node);
			}
		}

		super.append(...arrayOfNodes);
		return this;
	}

	/**
	 * Ajoute une node brute à l'arbre.
	 * @param node Node à ajouter.
	 * @returns Node ajoutée.
	 */
	appendChild<T extends Node>(node: T): T {
		return super.appendChild(node);
	}
	//#endregion Public Methods

	//#region Private Methods
	/**
	 * Initialise le focus : seul le premier élément est tabulable.
	 */
	#_initializeRovingTabindex(): void {
		const items = this.#_getAllItems();
		if (items.length === 0) return;

		const selected = items.find(i => i.getAttribute(ATTR_SELECTED) === 'true');
		items.forEach(i => i.setAttribute('tabindex', '-1'));

		const initial = selected || items[0];
		initial.setAttribute('tabindex', '0');
		this.#_focusedItem = initial;
	}

	/**
	 * Gestionnaire de sélection générique
	 * @param e Événement de clic
	 */
	@Autobind
	private _handleSelection(e: Event): void {
		// On cherche l'élément treeitem le plus proche de la cible du clic
		const target = (e.target as HTMLElement).closest(ROLE_ITEM) as HTMLElement;

		if (!target || target.getAttribute('is-virtual') === 'true') return;

		this.SelectItem(target);
	}

	#_initListeners() {
		this.#_listenKeyDown();
		this.#_listenClick();
		return this;
	}

	#_clearListeners() {
		this.removeEventListener('keydown', this._handleKeyDown);
		this.removeEventListener('click', this._handleSelection);
		return this;
	}

	#_listenKeyDown() {
		this.addEventListener('keydown', this._handleKeyDown);
	}

	#_listenClick() {
		this.addEventListener('click', this._handleSelection);
	}

	@Autobind
	private _handleKeyDown(e: KeyboardEvent): void {
		const current = this.#_focusedItem;

		if (!this.#_focusedItem?.isConnected) this.#_focusedItem = null;

		if (!current) return;

		const visibleItems = this.#_getVisibleItems();
		const index = visibleItems.indexOf(current);
		let next: HTMLElement | null = null;

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				next = visibleItems[index + 1] || null;
				break;
			case 'ArrowUp':
				e.preventDefault();
				next = visibleItems[index - 1] || null;
				break;
			case 'ArrowRight':
				e.preventDefault();
				// Si l'élément est repliable
				if (current.hasAttribute(ATTR_COLLAPSED)) {
					if (current.getAttribute(ATTR_COLLAPSED) === 'true') {
						current.setAttribute(ATTR_COLLAPSED, 'false');
					} else {
						next = visibleItems[index + 1] || null;
					}
				}
				break;
			case 'ArrowLeft':
				e.preventDefault();
				if (current.getAttribute(ATTR_COLLAPSED) === 'false') {
					current.setAttribute(ATTR_COLLAPSED, 'true');
				} else {
					const parent = current.parentElement?.closest(
						ROLE_ITEM,
					) as HTMLElement;
					if (parent) next = parent;
				}
				break;
			case 'Home':
				e.preventDefault();
				next = visibleItems[0];
				break;
			case 'End':
				e.preventDefault();
				next = visibleItems[visibleItems.length - 1];
				break;
			case 'Enter':
			case ' ':
				e.preventDefault();
				current.click();
				break;
		}

		if (next) this.#_updateFocus(next);
	}

	#_updateFocus(target: HTMLElement): void {
		if (this.#_focusedItem) {
			this.#_focusedItem.setAttribute('tabindex', '-1');
		}
		target.setAttribute('tabindex', '0');
		target.focus();
		this.#_focusedItem = target;
	}

	#_getAllItems(): HTMLElement[] {
		return Array.from(
			this.querySelectorAll(
				`${ROLE_ITEM}, bnum-tree-item, ${HTMLBnumFolder.TAG}`,
			),
		) as HTMLElement[];
	}

	#_getVisibleItems(): HTMLElement[] {
		return this.#_getAllItems().filter(item => {
			let parent = item.parentElement?.closest(ROLE_ITEM) as HTMLElement;
			while (parent) {
				if (parent.getAttribute(ATTR_COLLAPSED) === 'true') return false;
				parent = parent.parentElement?.closest(ROLE_ITEM) as HTMLElement;
			}
			return true;
		});
	}
	//#endregion Private Methods

	@Risky()
	static TryIncludeStyle(): Result<void> {
		const css = BnumElementInternal.ConstructCSSStyleSheet(style);

		document.adoptedStyleSheets = [...document.adoptedStyleSheets, css];

		return Result.Ok();
	}
}

HTMLBnumTree.TryIncludeStyle().tapError(e => {
	Log.error('HTMLBnumTree', 'Failed to include style', e);
});
